import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DocumentPreviewPane } from "components/Document/shared/DocumentPreviewPane";
import { useState } from "react";
import { prettyPrintDateMed } from "util/dates";

export interface ReplaceManifestDocumentDialogProps {
  open: boolean;
  /** The manifest's current source document — the one whose extracted data
   * gets replaced on confirm. */
  currentDocument: {
    id: string;
    title: string;
    fileExtension?: string | null;
    createdAt?: string | null;
    /** True when the current document's extracted data has a completed review
     * (isManifestDocumentReviewed) — replacing it discards that review. */
    reviewed: boolean;
  };
  /** The attached document about to be processed into the new data source. */
  newDocument: { id: string; title: string; fileExtension?: string | null };
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Replace-source confirmation for manifest documents. Processing a
 * non-source document replaces the manifest's document data, and until now the
 * confirm showed nothing about what was being replaced. This dialog previews
 * the current source PDF (with uploaded date and review status), always names
 * the incoming document, adds a Current/New toggle so the incoming copy can be
 * eyeballed too, and calls out explicitly when a completed review is about to
 * be discarded.
 *
 * Both PDFs already exist as attached documents before anything is replaced,
 * so this is preview-only — nothing is processed until confirm.
 */
export function ReplaceManifestDocumentDialog({
  open,
  currentDocument,
  newDocument,
  onClose,
  onConfirm,
}: ReplaceManifestDocumentDialogProps) {
  const [selected, setSelected] = useState<"current" | "new">("current");
  const selectedDoc = selected === "current" ? currentDocument : newDocument;

  const uploadedLabel = currentDocument.createdAt
    ? `Uploaded ${prettyPrintDateMed(currentDocument.createdAt)}`
    : null;
  const currentMeta = [uploadedLabel, currentDocument.reviewed ? "Reviewed" : "Not reviewed"]
    .filter(Boolean)
    .join(" • ");

  return (
    // ConfirmDialog fires its confirm on Enter anywhere in the dialog (a root
    // onKeyDown). That is unsafe for this destructive replace: an Enter aimed at
    // Cancel, the close X, the preview toggle, or the dialog surface would fire
    // the replace. ConfirmDialog renders those controls outside `children`, so a
    // guard on the content alone can't cover them — but React routes key events
    // from the portaled dialog through this wrapper in the React tree, so a
    // capture-phase guard here intercepts every Enter before it reaches
    // ConfirmDialog's handler. Each control still activates on its own Enter
    // (Process Document confirms, Cancel/X close), so only a deliberate confirm
    // goes through.
    <Box
      sx={{ display: "contents" }}
      onKeyDownCapture={(event) => {
        if (event.key === "Enter") event.stopPropagation();
      }}
    >
      <ConfirmDialog
        open={open}
        header="Replace manifest document data?"
        confirmText="Process Document"
        onClose={onClose}
        onConfirm={onConfirm}
        minWidth="min(720px, 90vw)"
        maxWidth="860px"
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Processing this document will replace the manifest document data from the current data
            source to this one. The original document won't be removed.
          </Typography>
          {/* Always name the incoming document, independent of the preview
              toggle, so the user can't confirm without seeing what they're
              replacing the current source with. */}
          <Typography variant="body2" color="text.secondary">
            Replacing with{" "}
            <Box
              component="span"
              sx={{ color: "text.primary", fontWeight: 500 }}
              title={newDocument.title}
            >
              {newDocument.title}
            </Box>
          </Typography>
          {currentDocument.reviewed && (
            <Alert severity="warning" variant="outlined">
              The current document's data has been reviewed. Replacing it discards that review, and
              this manifest will fall back to e-Manifest or vendor data until the new document's
              data is reviewed.
            </Alert>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box minWidth={0}>
              <Typography variant="subtitle2" noWrap title={selectedDoc.title}>
                {selectedDoc.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selected === "current" ? currentMeta : "Incoming document • Not processed yet"}
              </Typography>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selected}
              onChange={(_event, value: "current" | "new" | null) => {
                if (value) setSelected(value);
              }}
              aria-label="Document to preview"
            >
              <ToggleButton value="current">Current</ToggleButton>
              <ToggleButton value="new">New</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {/* Keep both previews mounted and toggle visibility rather than
            swapping one pane's document. The pane fetches network-only, so
            re-pointing a single instance would refetch the signed URL and
            flash the skeleton on every toggle. `visibility` (not
            `display: none`) preserves each pane's layout box so the PDF
            viewer can measure its container and render while hidden. */}
          <Box sx={{ position: "relative", height: 420 }}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                visibility: selected === "current" ? "visible" : "hidden",
              }}
            >
              <DocumentPreviewPane document={currentDocument} />
            </Box>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                visibility: selected === "new" ? "visible" : "hidden",
              }}
            >
              <DocumentPreviewPane document={newDocument} />
            </Box>
          </Box>
        </Stack>
      </ConfirmDialog>
    </Box>
  );
}
